// frontend/src/DateBox.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* === Helpers === */
function pad(n){ return n.toString().padStart(2, '0'); }
function isoToDisplay(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : '';
}
function displayToISO(display) {
  if (!display) return '';
  const m = String(display).match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}
function clampISO(iso, minISO, maxISO) {
  if (!iso) return iso;
  if (minISO && iso < minISO) return minISO;
  if (maxISO && iso > maxISO) return maxISO;
  return iso;
}
function daysInMonth(year, monthIndex) { return new Date(year, monthIndex + 1, 0).getDate(); }
function startOfMonthWeekday(year, monthIndex) { return new Date(year, monthIndex, 1).getDay(); }
function monthStartISO(y, mIndex){ return `${y}-${pad(mIndex+1)}-01`; }
function monthEndISO(y, mIndex){ return `${y}-${pad(mIndex+1)}-${pad(daysInMonth(y, mIndex))}`; }
function rangesOverlap(aStart, aEnd, bStart, bEnd){
  // Strings in YYYY-MM-DD can be lexicographically compared safely
  return !(aEnd < bStart || aStart > bEnd);
}
function yearHasValidDay(y, minISO, maxISO){
  if (!minISO && !maxISO) return true;
  const yStart = `${y}-01-01`;
  const yEnd   = `${y}-12-31`;
  const min = minISO || yStart;
  const max = maxISO || yEnd;
  return rangesOverlap(yStart, yEnd, min, max);
}
function monthHasValidDay(y, mIndex, minISO, maxISO){
  if (!minISO && !maxISO) return true;
  const mStart = monthStartISO(y, mIndex);
  const mEnd   = monthEndISO(y, mIndex);
  const min = minISO || mStart;
  const max = maxISO || mEnd;
  return rangesOverlap(mStart, mEnd, min, max);
}

/* === Higher-contrast, theme-aware inline styles === */
const COLOR = {
  text: 'var(--text, #111827)',
  textMuted: 'var(--text-muted, #374151)',
  surface: 'var(--surface, #ffffff)',
  surface2: 'var(--surface-2, #f3f4f6)',
  surface3: 'var(--surface-3, #e5e7eb)',
  border: 'var(--border, #d1d5db)',
  popShadow: '0 12px 28px rgba(0,0,0,.18)',
  selectedBg: 'var(--accent-weak, #e6f9ef)',
  selectedBorder: 'var(--accent, #16a34a)',
  hoverBg: 'var(--hover, #e5e7eb)'
};

const S = {
  root: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontWeight: 700, color: COLOR.text },
  wrap: { position: 'relative', display: 'flex', alignItems: 'stretch', gap: 6 },
  input: {
    flex: 1, padding: '10px 12px', borderRadius: 10,
    border: `1px solid ${COLOR.border}`,
    background: COLOR.surface, color: COLOR.text
  },
  btn: {
    border: `1px solid ${COLOR.border}`, borderRadius: 10,
    padding: '0 10px', background: COLOR.surface2, cursor: 'pointer', color: COLOR.text
  },
  pop: {
    position: 'absolute', zIndex: 20, top: 'calc(100% + 6px)', left: 0, width: 300,
    background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 12,
    boxShadow: COLOR.popShadow, padding: 10, color: COLOR.text
  },
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  navGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  navBtn: {
    background: COLOR.surface2, border: `1px solid ${COLOR.border}`,
    borderRadius: 8, padding: '4px 10px', cursor: 'pointer', color: COLOR.text
  },
  title: { fontWeight: 800, letterSpacing: '.2px', cursor: 'pointer' },
  grid7: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  dow: { textAlign: 'center', fontSize: 12, color: COLOR.textMuted, padding: '4px 0', fontWeight: 600 },
  cell: {
    textAlign: 'center', padding: '10px 0',
    background: COLOR.surface2, border: `1px solid ${COLOR.border}`,
    borderRadius: 10, cursor: 'pointer', color: COLOR.text, fontWeight: 600
  },
  cellSelected: { background: COLOR.selectedBg, borderColor: COLOR.selectedBorder, boxShadow: `inset 0 0 0 2px ${COLOR.selectedBorder}` },
  cellEmpty: { background: 'transparent', border: 'none' },
  cellDisabled: { background: COLOR.surface3, color: '#9ca3af', cursor: 'not-allowed', fontWeight: 500 }
};

export default function DateBox({
  label,
  valueISO, onChangeISO,
  placeholder,
  minISO, maxISO,
  disabled = false, required = false, name,
}) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('date.date');
  const resolvedPlaceholder = placeholder ?? t('date.formatYMD');
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('day'); // 'day' | 'year' | 'month'
  const today = new Date();

  const initYear = valueISO ? Number(valueISO.slice(0, 4)) : today.getFullYear();
  const initMonth = valueISO ? Number(valueISO.slice(5, 7)) - 1 : today.getMonth();
  const [viewYear, setViewYear] = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);

  // Year panel paging (12-year pages)
  const [yearPageStart, setYearPageStart] = useState(initYear - 5);

  useEffect(() => {
    if (valueISO && /^\d{4}-\d{2}-\d{2}$/.test(valueISO)) {
      const y = Number(valueISO.slice(0,4));
      const m = Number(valueISO.slice(5,7)) - 1;
      setViewYear(y);
      setViewMonth(m);
      setYearPageStart(y - 5);
    }
  }, [valueISO]);

  useEffect(() => {
    function onDocClick(e) {
      if (!open) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setMode('day');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const display = useMemo(() => isoToDisplay(valueISO), [valueISO]);
  const monthLabels = t('date.monthsShort', { returnObjects: true });
  const weekdayLabels = t('date.weekdaysShort', { returnObjects: true });

  // --- Day grid
  const dayGrid = useMemo(() => {
    const dim = daysInMonth(viewYear, viewMonth);
    const start = startOfMonthWeekday(viewYear, viewMonth);
    const cells = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) {
      const iso = `${viewYear}-${pad(viewMonth + 1)}-${pad(d)}`;
      const isDisabled = (minISO && iso < minISO) || (maxISO && iso > maxISO);
      cells.push({ d, iso, isDisabled });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth, minISO, maxISO]);

  // --- Month grid (for the chosen year)
  const monthGrid = useMemo(() => {
    return monthLabels.map((label, idx) => {
      const disabled = !monthHasValidDay(viewYear, idx, minISO, maxISO);
      return { label, idx, disabled };
    });
  }, [viewYear, minISO, maxISO, monthLabels]);

  // --- Year grid (12-year page)
  const yearGrid = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 12; i++) {
      const y = yearPageStart + i;
      const disabled = !yearHasValidDay(y, minISO, maxISO);
      arr.push({ y, disabled });
    }
    return arr;
  }, [yearPageStart, minISO, maxISO]);

  const onPrevMonth = () => {
    let y = viewYear, m = viewMonth - 1;
    if (m < 0) { m = 11; y--; }
    setViewYear(y); setViewMonth(m);
  };
  const onNextMonth = () => {
    let y = viewYear, m = viewMonth + 1;
    if (m > 11) { m = 0; y++; }
    setViewYear(y); setViewMonth(m);
  };

  const onSelectISO = (iso) => {
    if (!onChangeISO) return;
    onChangeISO(clampISO(iso, minISO, maxISO));
    setOpen(false);
    setMode('day');
  };

  const onManualChange = (e) => {
    const v = e.target.value;
    const iso = displayToISO(v);
    if (iso && onChangeISO) {
      onChangeISO(clampISO(iso, minISO, maxISO));
    } else if (v === '') {
      onChangeISO('');
    }
  };

  const openYearPicker = () => setMode('year');

  const onYearPrevPage = () => setYearPageStart(yearPageStart - 12);
  const onYearNextPage = () => setYearPageStart(yearPageStart + 12);
  const onSelectYear = (y) => {
    setViewYear(y);
    setMode('month');
  };
  const onSelectMonth = (mIdx) => {
    setViewMonth(mIdx);
    setMode('day');
  };

  return (
    <div ref={containerRef} style={S.root}>
      {resolvedLabel && <label style={S.label} htmlFor={name}>{resolvedLabel}</label>}
      <div style={S.wrap}>
        <input
          id={name}
          name={name}
          type="text"
          inputMode="numeric"
          placeholder={resolvedPlaceholder}
          value={display}
          onChange={onManualChange}
          onFocus={() => !disabled && setOpen(true)}
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
          required={required}
          style={S.input}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          aria-label={t('date.openCalendar')}
          tabIndex={-1}
          style={S.btn}
        >
          📅
        </button>

        {open && !disabled && (
          <div role="dialog" aria-modal="false" style={S.pop}>
            {/* NAV */}
            <div style={S.nav}>
              <div style={S.navGroup}>
                {mode === 'day' && (
                  <>
                    <button type="button" onClick={onPrevMonth} aria-label={t('date.previousMonth')} style={S.navBtn}>‹</button>
                    <button type="button" onClick={onNextMonth} aria-label={t('date.nextMonth')} style={S.navBtn}>›</button>
                  </>
                )}
                {mode === 'year' && (
                  <>
                    <button type="button" onClick={onYearPrevPage} aria-label={t('date.previousYears')} style={S.navBtn}>‹</button>
                    <button type="button" onClick={onYearNextPage} aria-label={t('date.nextYears')} style={S.navBtn}>›</button>
                  </>
                )}
                {mode === 'month' && (
                  <>
                    <button type="button" onClick={() => setMode('year')} aria-label={t('date.backToYear')} style={S.navBtn}>‹</button>
                    <button type="button" onClick={() => setMode('year')} aria-label={t('date.forwardToYear')} style={S.navBtn} disabled>›</button>
                  </>
                )}
              </div>

              {/* Title click -> Year picker */}
              {mode === 'day' && (
                <div style={S.title} onClick={openYearPicker}>
                  {monthLabels[viewMonth]} {viewYear}
                </div>
              )}
              {mode === 'month' && (
                <div style={S.title} onClick={() => setMode('year')}>
                  {viewYear}
                </div>
              )}
              {mode === 'year' && (
                <div style={{...S.title, cursor: 'default'}}>
                  {yearPageStart} – {yearPageStart + 11}
                </div>
              )}

              <div style={{ width: 48 }} /> {/* spacer */}
            </div>

            {/* CONTENT */}
            {mode === 'day' && (
              <>
                <div style={S.grid7}>
                  {weekdayLabels.map(d => (
                    <div key={d} style={S.dow}>{d}</div>
                  ))}
                  {dayGrid.map((cell, idx) => {
                    if (!cell) return <div key={idx} style={S.cellEmpty} />;
                    const isSelected = valueISO === cell.iso;
                    const style = {
                      ...S.cell,
                      ...(isSelected ? S.cellSelected : {}),
                      ...(cell.isDisabled ? S.cellDisabled : {})
                    };
                    return (
                      <button
                        key={idx}
                        type="button"
                        disabled={cell.isDisabled}
                        onClick={() => onSelectISO(cell.iso)}
                        style={style}
                      >
                        {cell.d}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {mode === 'month' && (
              <div style={S.grid3}>
                {monthGrid.map(({ label, idx, disabled }) => {
                  const isSelected = (valueISO && Number(valueISO.slice(0,4)) === viewYear && Number(valueISO.slice(5,7)) - 1 === idx);
                  const style = {
                    ...S.cell,
                    ...(isSelected ? S.cellSelected : {}),
                    ...(disabled ? S.cellDisabled : {})
                  };
                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={disabled}
                      onClick={() => onSelectMonth(idx)}
                      style={style}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            {mode === 'year' && (
              <div style={S.grid4}>
                {yearGrid.map(({ y, disabled }) => {
                  const isSelected = (valueISO && Number(valueISO.slice(0,4)) === y);
                  const style = {
                    ...S.cell,
                    ...(isSelected ? S.cellSelected : {}),
                    ...(disabled ? S.cellDisabled : {})
                  };
                  return (
                    <button
                      key={y}
                      type="button"
                      disabled={disabled}
                      onClick={() => onSelectYear(y)}
                      style={style}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
